import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import DropdownMenu from '../dropdown-menu/DropdownMenu';
import MediaDetailModal from '../media-preview/components/MediaDetailModal';
import MediaTranscriptionModal from '../media-preview/components/MediaTranscriptionModal';
import { MediaPreviewHandle } from '../../types/media-preview';
import { AcrScanStatus, AcrScanResult } from '../../types/acr';
import { getAcrIconMeta } from '../../utils/acr';
import { buildAudioMenuItems } from '../../utils/audioMenu';
import { downloadMediaFile } from '../../utils/downloadFile';

import WaveformCanvas from './WaveformCanvas';
import { usePeaks } from './core/usePeaks';
import { useAudioTransport } from './core/useAudioTransport';
import { estimatePeaksDuration, formatAudioTime } from './core/peaks';

/**
 * プレシジョンプレビュー（Preview 画面などの本編集用オーディオプレイヤー）。
 *
 * 正確な確認に最適化した 3 行レイアウト:
 * 1. 再生/停止（左）—— ACR・三点リーダー（右端）
 * 2. ステレオ波形（ホバー時刻線 + クリック/ドラッグシーク。ズームはタイムラインが担うため無し）
 * 3. 現在時間（左）—— 総時間（右）
 *
 * オプションモード（optionLabel 指定時 / 比較スライド用）:
 * 再生ボタンが A / B / C のレターバッジになり、クリックで**頭出し再生**と同時に
 * そのオプションが選択される（onOptionSelect）。再生中は停止アイコンに変わる。
 * 波形クリック（シーク）でも選択が切り替わる。シーク位置からの再生は
 * 従来どおり Space / 再生ハンドル経由で行える。
 *
 * 波形は Lambda 事前計算の peaks JSON を優先し、無い旧ファイルは
 * ブラウザ側 decode にフォールバックする（正確さ優先）。
 * 描画は自前 Canvas（WaveformCanvas）のため、リサイズは純関数再描画で安定。
 */

export interface PrecisionAudioPlayerProps {
  src: string;
  /** Lambda 事前計算の peaks JSON URL */
  waveformUrl?: string | null;
  /** peaks キャッシュキー（ファイル ID 等）。未指定時は waveformUrl / src を使用 */
  cacheKey?: string;
  fileName?: string;
  downloadUrl?: string;
  onDownload?: () => void;
  showMenu?: boolean;
  /** ACR バッジを表示する（既定 true）。モーダル内など遷移元と重複する場面では false */
  showAcrBadge?: boolean;
  /**
   * 画面外に出たとき自動一時停止する（既定 true）。
   * タイムラインモードなどプレビューを畳んで再生を継続したい場面では false
   */
  pauseWhenOffscreen?: boolean;
  /** オプションモード: 再生ボタンに表示するレター（A / B / C…）。停止中のみレター表示 */
  optionLabel?: string;
  /** オプションモード: 選択中の強調（レターバッジを青にする） */
  optionSelected?: boolean;
  /** オプションモード: 再生開始時にそのオプションを選択状態にする */
  onOptionSelect?: () => void;
  /** メタデータ or 波形の準備完了時に一度だけ呼ばれる */
  onLoaded?: () => void;
  onPlayStateChange?: (_playing: boolean) => void;
  // ACR 楽曲認識
  fileId?: string;
  acrScanStatus?: AcrScanStatus;
  acrScanResult?: AcrScanResult | null;
  acrScannedAt?: string | null;
  className?: string;
  style?: React.CSSProperties;
}

/** 波形の描画高さ（2ch 分割表示で 1ch あたり 48px になる高さ） */
const WAVEFORM_HEIGHT = 96;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const PlayButton = styled.span`
  font-size: 32px;
  color: var(--soremo-blue, #009ace);
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  user-select: none;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0076a5;
    scale: 1.1;
  }
`;

/**
 * オプションモードのレターバッジ兼再生ボタン。
 * 比較スライドの連番バッジ（EntryLetterBadge）と同デザインの 32px 丸
 * （非選択: 黒 / 選択・クライアント最終選択: 青）。
 */
const OptionLetterButton = styled.button.attrs({ className: 'bodytext' })<{
  $active: boolean;
}>`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  padding: 0;
  line-height: 100%;
  letter-spacing: 0;
  user-select: none;

  border-radius: 50%;
  border: 1px solid var(--soremo-border);

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${p => (p.$active ? 'var(--soremo-blue)' : 'var(--soremo-gray)')};
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover {
    transform: scale(1.08);
  }
`;

const HeaderSpacer = styled.div`
  flex: 1;
  min-width: 0;
`;

const AcrBadge = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 1;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const TimeRow = styled.div.attrs({
  className: 'bodytext-11',
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--soremo-light-gray);
  line-height: 1;
  user-select: none;
  font-variant-numeric: tabular-nums;
`;

const PrecisionAudioPlayer = forwardRef<MediaPreviewHandle, PrecisionAudioPlayerProps>(({
  src,
  waveformUrl,
  cacheKey,
  fileName,
  downloadUrl,
  onDownload,
  showMenu = true,
  showAcrBadge = true,
  pauseWhenOffscreen = true,
  optionLabel,
  optionSelected = false,
  onOptionSelect,
  onLoaded,
  onPlayStateChange,
  fileId,
  acrScanStatus,
  acrScanResult,
  acrScannedAt,
  className,
  style,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);

  // 正確さ優先: 波形 JSON が無い旧ファイルはブラウザ側 decode にフォールバック
  const { peaks } = usePeaks({
    cacheKey: cacheKey ?? fileId ?? waveformUrl ?? src,
    waveformUrl,
    fallbackSrc: src,
  });

  const transport = useAudioTransport({
    src,
    preload: 'metadata',
    initialDuration: estimatePeaksDuration(peaks),
    onPlayStateChange,
  });
  const { isPlaying, currentTime, duration } = transport;

  // --- ロード完了通知（メタデータ確定 or peaks 到着で一度だけ） ---
  const hasCalledOnLoadedRef = useRef(false);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => {
    if (hasCalledOnLoadedRef.current) return;
    if (duration > 0 || peaks) {
      hasCalledOnLoadedRef.current = true;
      onLoadedRef.current?.();
    }
  }, [duration, peaks]);

  // --- 画面外に出たら一時停止、戻ったら再開（スライドカルーセル対応） ---
  // pauseWhenOffscreen=false の場合（タイムラインモードでプレビューを畳む等）は
  // ジオメトリが消えても再生を継続する。
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const wasPlayingBeforeHiddenRef = useRef(false);
  useEffect(() => {
    if (!pauseWhenOffscreen) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.intersectionRatio < 0.1) {
            if (transportRef.current.isPlaying) {
              wasPlayingBeforeHiddenRef.current = true;
              transportRef.current.pause();
            }
          } else if (wasPlayingBeforeHiddenRef.current) {
            wasPlayingBeforeHiddenRef.current = false;
            transportRef.current.play();
          }
        });
      },
      { threshold: [0, 0.1] },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [pauseWhenOffscreen]);

  // --- メニュー ---
  const handleDetailClick = useCallback(() => {
    setShowDetailModal(true);
  }, []);

  const handleDownloadClick = useCallback(() => {
    if (!downloadMediaFile({ downloadUrl, src, fileName, fallbackName: 'audio' })) return;
    onDownload?.();
  }, [downloadUrl, src, fileName, onDownload]);

  const handleTranscribeClick = useCallback(() => {
    setShowTranscriptionModal(true);
  }, []);

  const menuItems = useMemo(() => buildAudioMenuItems({
    onDetailInfo: handleDetailClick,
    onDownload: handleDownloadClick,
    onTranscribe: fileId ? handleTranscribeClick : undefined,
    acrScanStatus,
  }), [handleDetailClick, handleDownloadClick, handleTranscribeClick, fileId, acrScanStatus]);

  const acrIcon = getAcrIconMeta(acrScanStatus);

  // --- 外部 API 公開（Preview ページ・タイムラインパネルからの再生制御用） ---
  useImperativeHandle(ref, () => ({
    play: async () => transport.play(),
    pause: () => transport.pause(),
    seek: (seconds: number) => transport.seekBy(seconds),
    seekTo: (seconds: number) => transport.seekTo(seconds),
    getCurrentTime: () => transport.getAudioElement()?.currentTime ?? 0,
    getDuration: () => {
      const el = transport.getAudioElement();
      return el && isFinite(el.duration) ? el.duration : transport.duration;
    },
    clickToggle: () => transport.toggle(),
    getMediaElement: () => transport.getAudioElement(),
  }), [transport]);

  return (
    <>
      <Container ref={containerRef} className={className} style={style}>
        <HeaderRow>
          {optionLabel && !isPlaying ? (
            <OptionLetterButton
              type="button"
              $active={optionSelected}
              aria-label={`${optionLabel}案を頭から再生`}
              onClick={() => {
                // バッジからの再生は必ず頭出し + そのオプションを選択。
                // （シーク位置からの再生は波形クリック → Space / ハンドル経由で行う）
                onOptionSelect?.();
                transport.seekTo(0);
                transport.play();
              }}
            >
              {optionLabel}
            </OptionLetterButton>
          ) : (
            <PlayButton
              className="material-symbols-rounded"
              onClick={transport.toggle}
            >
              {isPlaying ? 'pause_circle' : 'play_circle'}
            </PlayButton>
          )}
          <HeaderSpacer />
          {showAcrBadge && acrIcon.show && (
            <AcrBadge
              className="material-symbols-rounded"
              style={{ color: acrIcon.color }}
              onClick={handleDetailClick}
            >
              error
            </AcrBadge>
          )}
          {showMenu && <DropdownMenu items={menuItems} />}
        </HeaderRow>
        <WaveformCanvas
          peaks={peaks}
          currentTime={currentTime}
          duration={duration}
          height={WAVEFORM_HEIGHT}
          channelMode="split"
          onSeek={seconds => {
            // オプションモードでは再生位置の指定で選択も切り替える（通常時は no-op）
            onOptionSelect?.();
            transport.seekTo(seconds);
          }}
          showHoverIndicator
        />
        <TimeRow>
          <span>{formatAudioTime(currentTime)}</span>
          <span>{formatAudioTime(duration)}</span>
        </TimeRow>
      </Container>
      {showDetailModal && (
        <MediaDetailModal
          onClose={() => setShowDetailModal(false)}
          fileName={fileName}
          acrScanResult={acrScanResult}
          fileId={fileId}
          acrScanStatus={acrScanStatus}
          acrScannedAt={acrScannedAt}
        />
      )}
      {showTranscriptionModal && fileId && (
        <MediaTranscriptionModal
          fileId={fileId}
          fileName={fileName}
          onClose={() => setShowTranscriptionModal(false)}
          onSeek={seconds => transport.seekTo(seconds)}
        />
      )}
    </>
  );
});

PrecisionAudioPlayer.displayName = 'PrecisionAudioPlayer';

export default PrecisionAudioPlayer;
